import { MuiAlert, tryParseJSONArray, tryParseJSONObject } from '@fox/atoms';
import { BasicImportWizard, MapOption, MatchOption, ValidationResult, ValidationStatus } from '@fox/import-core';
import { useMediaQuery } from '@mui/material';
import { useRef } from 'react';
import { useContentContext } from '../../content-context';
import { useCreateImportBatch, useUpsertImportDoc } from '../mutation-hooks/mutation-implementations';
import { useGetLinkedOrganisationDetails } from '../query-hooks/query-implementations';
import { ImporterProps, ImportFinancialOptions } from './contracts';
import { GuideLines } from './guidelines';
import { ImportButton } from './import-button';
import { ImportOverview } from './import-overview';
import { ImporterContextProvider, useImporterContext } from './importer-context';
import { ImportReview } from './review';

export function InnerImporter() {
	const { contentId, requestToken } = useContentContext();
	const { batchId, linkedOrganisationDetails: importSettings, mapTypes, matchOptions, financialOptions } = useImporterContext();
	const recordIdRef = useRef<number>(1); //just to give the records a unique id
	const { mutate: createImportBatch } = useCreateImportBatch({ requestToken });
	const { mutate: upsertImportDoc } = useUpsertImportDoc({ requestToken });

	return (
		<BasicImportWizard<Record<string, unknown>, Record<string, unknown>>
			mapTypes={mapTypes}
			matchOptions={matchOptions}
			importingLabel="Preparing import ..."
			performValidateChunk={async (chunk, mapAlgorithm, chunkIndex, chunkSize) => {
				if (chunkIndex === 0) {
					await upsertImportDoc({
						contentId,
						id: batchId,
						organisationId: importSettings.organisationId,
						completed: false,
						pricePerRowInCents: financialOptions.pricePerRow
					});
				}
				await createImportBatch({
					chunkId: chunkIndex.toString(),
					contentId,
					data: JSON.stringify(chunk),
					batchId: batchId
				});
				//we're only pretend validating for now in order to comply with the typing rules of the importer
				const validatedChunk: ValidationResult<Record<string, unknown>>[] = chunk.map(c => {
					return {
						result: ValidationStatus.NotFound,
						...c
					};
				});

				return validatedChunk;
			}}
			format={(_, data, map) => {
				const formattedData = Object.entries(data).reduce((prev, [key, value]) => {
					const mapKey = Object.entries(map).find(([k, v]) => v === key)?.[0];
					if (mapKey && mapKey !== '!') {
						prev[mapKey] = value;
					}
					return prev;
				}, {});
				formattedData['record_id'] = recordIdRef.current.toString();
				recordIdRef.current++;
				//add default values to the record
				return { ...formattedData, ...(financialOptions.defaultFieldValues ?? {}) };
			}}
			ReviewComponent={ImportReview}
			importChunk={async (chunk, chunkNo) => {
				return true;
			}}
			guidelines={<GuideLines />}
			category="member_portal"
			customImportButton={ImportButton}
			importOverview={ImportOverview}
		/>
	);
}

export function Importer({ mapTypes, matchOptions, guideLineText, financialOptions, reviewText, redirectUrl }: ImporterProps) {
	const { contentId, requestToken, isLoggedIn } = useContentContext();
	const { data: linkedOrganisationDetails, loading, error } = useGetLinkedOrganisationDetails({ contentId, requestToken });
	const isMobile = useMediaQuery('(max-width: 800px)');

	if (isMobile) {
		return <MuiAlert severity="info">The importer is incompatble with your device</MuiAlert>;
	}

	if (!isLoggedIn) {
		return <MuiAlert severity="error">You need to be logged in to use the importer</MuiAlert>;
	}

	if (loading) {
		return <MuiAlert severity="info">Loading...</MuiAlert>;
	}

	if (error) {
		return <MuiAlert severity="error">Error loading import settings</MuiAlert>;
	}

	if (!linkedOrganisationDetails?.hasOrganisation) {
		return (
			<MuiAlert severity="warning">
				Your account is not correctly configured for importing. Please speak to an administrator at your Organisation
			</MuiAlert>
		);
	}

	const parsedFinancialOptions: ImportFinancialOptions = tryParseJSONObject(financialOptions);
	const parsedMapTypes: MapOption[] = tryParseJSONArray(mapTypes);
	const parsedMatchOptions: MatchOption[] = tryParseJSONArray(matchOptions);

	if (!parsedMatchOptions?.length || !parsedMapTypes?.length || !parsedFinancialOptions.pricePerRow) {
		return <MuiAlert severity="error">Importer not correctly configured</MuiAlert>;
	}

	return (
		<ImporterContextProvider
			matchOptions={parsedMatchOptions}
			mapTypes={parsedMapTypes}
			financialOptions={parsedFinancialOptions}
			linkedOrganisationDetails={linkedOrganisationDetails}
			guideLineText={guideLineText}
			reviewText={reviewText}
			redirectUrl={redirectUrl}
		>
			<InnerImporter />
		</ImporterContextProvider>
	);
}
